// Regra de fechamento mensal do resultado comercial ("Aprovados").
//
// Fonte única para PerformanceDashboard.tsx e AnalyticsComercialView.tsx — as duas telas
// precisam reportar exatamente o mesmo número de "Aprovados" para o mesmo mês/canal.
//
// Decisões que contam como "Termo Aprovado" (realizado oficial).
export const APPROVED_DECISIONS = ['Aprovado', 'Aprovado com Ressalvas'];

// Carência: uma aprovação decidida nos primeiros dias do mês seguinte ainda é
// contabilizada no resultado do mês anterior (tempo de análise/backoffice não deve
// distorcer o fechamento do mês em que o cliente efetivamente entrou/avançou no funil).
export const DIAS_CARENCIA_FECHAMENTO = 3;

export interface JanelaFechamento {
    inicioISO: string;
    fimISO: string;
    mes: number;
    ano: number;
}

/**
 * Janela de fechamento do mês `mes`/`ano`: do dia (diasCarencia+1) do próprio mês até o
 * dia `diasCarencia` do mês seguinte (23:59:59.999) — uma partição sem sobreposição, para
 * que cada `decisao_final_at` caia em exatamente um mês de fechamento.
 *
 * Ex.: com carência de 3 dias, o fechamento de julho vai de 04/07 até 03/08 — uma decisão
 * tomada em 02/08 (dentro da carência) ainda conta para julho.
 */
export function janelaFechamento(mes: number, ano: number, diasCarencia: number = DIAS_CARENCIA_FECHAMENTO): JanelaFechamento {
    const inicio = new Date(ano, mes - 1, diasCarencia + 1, 0, 0, 0, 0);
    const fim = new Date(ano, mes, diasCarencia, 23, 59, 59, 999);
    return { inicioISO: inicio.toISOString(), fimISO: fim.toISOString(), mes, ano };
}

/** true se `decisaoISO` pertence ao fechamento de `mes`/`ano` dada a carência. */
export function pertenceAoFechamento(decisaoISO: string | null | undefined, mes: number, ano: number, diasCarencia: number = DIAS_CARENCIA_FECHAMENTO): boolean {
    if (!decisaoISO) return false;
    const { inicioISO, fimISO } = janelaFechamento(mes, ano, diasCarencia);
    return decisaoISO >= inicioISO && decisaoISO <= fimISO;
}

export interface AnaliseCadastralRow {
    id: number;
    responsavel_id?: string | null;
    consumo_medio_kwh?: number;
    status?: string | null;
    analise_cadastral?: Array<{ decisao_final?: string | null; decisao_final_at?: string | null }>;
}

/**
 * Data da última decisão de análise cadastral aprovada (em APPROVED_DECISIONS) de uma
 * negociação, ou `null` se ela nunca foi aprovada. Sem exigir mês/ano — é a data crua do
 * evento, pra quem precisa só saber "quando essa aprovação aconteceu" (ex.: contar
 * aprovações por qualquer período selecionado, não só um mês de fechamento).
 */
export function ultimaAprovacaoAt(row: AnaliseCadastralRow): string | null {
    const analises = row.analise_cadastral || [];
    if (analises.length === 0) return null;
    const ultima = [...analises].sort((a, b) => {
        const ta = a.decisao_final_at ? new Date(a.decisao_final_at).getTime() : 0;
        const tb = b.decisao_final_at ? new Date(b.decisao_final_at).getTime() : 0;
        return tb - ta;
    })[0];
    if (!ultima?.decisao_final || !APPROVED_DECISIONS.includes(ultima.decisao_final)) return null;
    return ultima.decisao_final_at || null;
}

/**
 * Mesma data de `ultimaAprovacaoAt`, mas invalidada quando `status` mostra que a negociação
 * foi perdida ou reprovada DEPOIS da aprovação — marcar como perdido (LossReasonModal.tsx) é
 * um UPDATE incondicional de `status` que não toca em `analise_cadastral`, então a decisão
 * aprovada continua no histórico mesmo sem valer mais como resultado realizado.
 */
export function aprovacaoAindaValida(row: AnaliseCadastralRow): string | null {
    if (row.status && STATUS_SEM_APROVACAO_VALIDA.includes(row.status)) return null;
    return ultimaAprovacaoAt(row);
}

/**
 * Perdida/reprovada depois da aprovação, ou de volta à fila da análise: em
 * `termo_assinado` (aguardando análise ou reanálise) e `termo_analise` (pendência)
 * a decisão gravada é rascunho ou ficou para trás. Caso real (20/10/2026): a
 * negociação 9554 do Benito tinha "Aprovado com Ressalvas" salvo no passo 4, sem
 * a análise concluída, e já contava como aprovada no Resultado do mês do Kanban.
 */
const STATUS_SEM_APROVACAO_VALIDA = ['perdido', 'termo_reprovado', 'termo_assinado', 'termo_analise'];

/**
 * Filtra `rows` para as negociações efetivamente aprovadas dentro do fechamento de
 * `mes`/`ano`, no formato usado pelo cálculo de ritmo/projeção (id, responsavel_id, kwh,
 * date). Fonte única consumida pelo bloco de Meta x Realizado do Analytics e pela
 * exportação executiva, pra não reimplementar a mesma lógica de "pega a decisão mais
 * recente" em cada lugar.
 */
export function linhasAprovadasNoFechamento(
    rows: AnaliseCadastralRow[],
    mes: number,
    ano: number,
    diasCarencia: number = DIAS_CARENCIA_FECHAMENTO,
): Array<{ id: number; responsavel_id: string | null; kwh: number; date: string | null }> {
    const out: Array<{ id: number; responsavel_id: string | null; kwh: number; date: string | null }> = [];
    rows.forEach(r => {
        const dataAprovacao = aprovacaoAindaValida(r);
        if (!pertenceAoFechamento(dataAprovacao, mes, ano, diasCarencia)) return;
        out.push({ id: r.id, responsavel_id: r.responsavel_id || null, kwh: r.consumo_medio_kwh || 0, date: dataAprovacao });
    });
    return out;
}

/**
 * Quando o intervalo `dataInicio`..`dataFim` (YYYY-MM-DD) corresponde a exatamente um
 * mês-calendário — usado por telas com date-picker livre (ex.: PerformanceDashboard, o
 * período personalizado do Analytics) pra decidir se a carência de fechamento se aplica.
 * Aceita as duas convenções comuns de "fim" que as pessoas digitam pra dizer "esse mês
 * inteiro": dia 1 até o último dia do próprio mês (01/07→31/07, fim inclusivo) OU dia 1 até
 * o dia 1 do mês seguinte (01/07→01/08, fim exclusivo). Em ambos os casos o mês resolvido é
 * sempre o mês de início. Fora dessas duas formas, não há "mês de fechamento" bem definido.
 */
export function mesUnicoDeIntervalo(dataInicio: string, dataFim: string): { mes: number; ano: number } | null {
    const start = new Date(dataInicio + 'T00:00:00');
    const end = new Date(dataFim + 'T00:00:00');
    if (start.getDate() !== 1) return null;

    const mesmoDia = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

    const ultimoDiaDoMesDeInicio = new Date(start.getFullYear(), start.getMonth() + 1, 0);
    const primeiroDiaDoMesSeguinte = new Date(start.getFullYear(), start.getMonth() + 1, 1);

    if (mesmoDia(end, ultimoDiaDoMesDeInicio) || mesmoDia(end, primeiroDiaDoMesSeguinte)) {
        return { mes: start.getMonth() + 1, ano: start.getFullYear() };
    }
    return null;
}
